import { useEffect, useState } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { type Editor } from '@ckeditor/ckeditor5-core';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import * as yup from 'yup';
import { useRef } from 'react';
import { IconButton, Input } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  MenuItem,
  Skeleton,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { yupResolver } from '@hookform/resolvers/yup';

import { CustomField } from '@bandido/shared/types';

import { ControlledTextField } from '@frontend/features/common/components/ControlledTextField';
import { ControlledSelect } from '@frontend/features/common/components/ControlledSelect';
import {
  NEW_SUPPORT_FORM_ID,
  useCreateCaseSupporForm,
  useGetSupportFormById,
} from '@frontend/features/agencies/queries';
import { SchemaGenerator, SupportForm } from '@frontend/features/support-forms/components';
import { useGetWorkflowsByAgency } from '@frontend/features/workflows/queries';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { QueryKeys } from '@frontend/features/common/types';
import { DynamicFieldsProvider } from '@frontend/features/support-forms/components/DynamicFieldsContext';

import { routes } from '../routes';

const resolver = yupResolver(
  yup.object().shape({
    title: yup.string().required('common.form.required'),
    description: yup.string().required('common.form.required'),
    customLogoUrl: yup.string().url('common.form.url'),
    workflow: yup.string().required('common.form.required'),
  }),
);

const FileInput = styled(Input)(({ theme }) => ({
  width: '100%',
  backgroundColor: theme.palette.field.main,
  padding: 8,
  borderRadius: 8,
  '&:before': {
    content: 'none',
  },
}));

const PreviewImage = styled('img')({
  maxHeight: 160,
  borderRadius: 4,
  objectFit: 'contain',
});

export const SupportFormEdit = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const { addToast } = useSnackBar();
  const { id: agencyId, supportFormId } = useParams();
  const { t } = useTranslation();
  const [selectedImage, setSelectedImage] = useState<File>();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({ resolver });

  const [fields, setFields] = useState<CustomField[]>([]);

  const { data: form, isLoading: formLoading } = useGetSupportFormById({
    id: supportFormId,
  });

  const { data: workflows, isLoading: workflowsLoading } = useGetWorkflowsByAgency({
    agencyId: agencyId,
    search: '',
  });

  const { title, description, workflow, customLogoUrl } = watch();

  const { mutate: save, isLoading: isSaving } = useCreateCaseSupporForm({
    onSuccess: () => {
      addToast({
        color: 'success',
        message: t('supportForms.view.saved'),
      });
      navigate(
        `${routes.dashboard.agencyView.root.replace(':id', String(agencyId))}/${
          routes.dashboard.agencyView.supportForm
        }`,
      );
      queryClient.invalidateQueries([QueryKeys.getSupportFormsByAgency]);
    },
    onError: () => {
      addToast({
        color: 'error',
        message: t('supportForms.view.error'),
      });
    },
  });

  const handleUpdateFields = (newFields: CustomField[]) => {
    setFields(newFields);
  };

  const onSubmit = async (data: any) => {
    if (agencyId) {
      save({
        id: supportFormId || undefined,
        title: data.title,
        logo: selectedImage,
        description: data.description,
        customLogoUrl: data.customLogoUrl || '',
        agencyId: +agencyId,
        workflowId: data.workflow,
        customizableFields: JSON.stringify(fields),
      });
    }
  };

  useEffect(() => {
    if (form) {
      reset({
        title: form.title,
        description: form.description,
        workflow: form.workflowId,
        customLogoUrl: form.customLogoUrl,
      });
      setPreviewUrl(form.internalLogoUrl);
      try {
        setFields(JSON.parse(form.customizableFields));
      } catch (error) {
        console.log('Error trying to parse custom fields:', error);
      }
    }
  }, [form]);

  if (formLoading) {
    return (
      <Skeleton
        sx={{
          backgroundColor: theme.palette.field.main,
          maxWidth: theme.spacing(10),
        }}
      />
    );
  }

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const [file] = event.target.files || [];
    if (file && file.type.startsWith('image/')) {
      setSelectedImage(file);
      const imageUrl = URL.createObjectURL(file);
      setPreviewUrl(imageUrl);
    }
  };

  const handleClearImage = () => {
    setSelectedImage(undefined);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <DynamicFieldsProvider fields={fields} onUpdateFields={handleUpdateFields}>
      <Box>
        <Card
          sx={{
            boxShadow: 'none',
            p: theme.spacing(2),
            borderRadius: theme.spacing(3),
          }}
          elevation={2}
        >
          <CardHeader
            title={
              <Typography variant="h6">
                {supportFormId === NEW_SUPPORT_FORM_ID
                  ? t('supportForms.view.titleCreate')
                  : t('supportForms.view.titleUpdate')}
              </Typography>
            }
          />
          <CardContent>
            <Box
              component="form"
              sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing(3),
                mt: theme.spacing(3),
              }}
              onSubmit={handleSubmit(onSubmit)}
            >
              <ControlledTextField
                required
                fullWidth
                control={control}
                name="title"
                label={t('supportForms.fields.titleField')}
                placeholder={t('common.form.onlyLetters')}
                errorMessage={errors.title?.message ? t(errors.title?.message) : undefined}
                disabled={supportFormId !== NEW_SUPPORT_FORM_ID}
              />

              <ControlledSelect
                required
                fullWidth
                variant="outlined"
                name="workflow"
                size="small"
                label="Workflow"
                control={control}
                disabled={workflowsLoading}
                placeholder={t('common.form.select')}
                errorMessage={errors.workflow?.message ? t(errors.workflow?.message) : undefined}
              >
                {workflows?.items.length ? (
                  workflows?.items.map(({ id, name }) => (
                    <MenuItem key={id} value={id}>
                      {name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem key="empty">{t('supportForms.view.noWorkflows')}</MenuItem>
                )}
              </ControlledSelect>

              <ControlledTextField
                fullWidth
                control={control}
                name="customLogoUrl"
                label={t('supportForms.fields.customLogoUrl')}
                helperText={t('supportForms.fields.customLogoUrlHelper')}
                placeholder={t('common.form.onlyLetters')}
                errorMessage={errors.customLogoUrl?.message ? t(errors.customLogoUrl?.message) : undefined}
              />

              <Box mb={2}>
                <Typography variant="caption" component="label" htmlFor="support-logo" gutterBottom>
                  {t('supportForms.fields.customLogoUrl')}
                </Typography>
                <Box position="relative">
                  <FileInput
                    id="support-logo"
                    type="file"
                    inputRef={fileInputRef}
                    onChange={handleImageChange}
                    inputProps={{ accept: 'image/*' }}
                  />
                  {previewUrl && (
                    <IconButton
                      onClick={handleClearImage}
                      size="small"
                      title={t('common.clear')}
                      sx={{
                        position: 'absolute',
                        right: 8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>

                {previewUrl && (
                  <Box mt={2}>
                    <PreviewImage src={previewUrl} />
                  </Box>
                )}
              </Box>

              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: theme.spacing(2),
                  mt: theme.spacing(2),
                }}
              >
                <Typography variant="subtitle2" color={theme.palette.disabled.main}>
                  {t('supportForms.fields.descriptionField')}
                </Typography>
                <Controller
                  name="description"
                  control={control}
                  rules={{
                    required: t('common.form.required'),
                  }}
                  render={({ field }) => (
                    <Box
                      sx={{
                        width: '100%',
                        borderRadius: theme.spacing(1),
                        backgroundColor: theme.palette.field.main,
                        '&& .ck-editor__main': {
                          border: 'none',
                        },

                        '.ck-editor__editable, .ck.ck-toolbar': {
                          backgroundColor: 'transparent !important',
                        },
                      }}
                    >
                      <CKEditor
                        editor={ClassicEditor}
                        data={field.value}
                        onChange={(_: any, editor: Editor) => {
                          const data = editor.getData();
                          field.onChange(data);
                        }}
                      />
                    </Box>
                  )}
                />
              </Box>

              <Divider sx={{ my: theme.spacing(2) }} />

              <SchemaGenerator />

              <Box display="flex" justifyContent="flex-end">
                <LoadingButton
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={!title && !description && !fields.length}
                  loading={isSaving}
                >
                  {t('common.save')}
                </LoadingButton>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card
          elevation={2}
          sx={{
            my: theme.spacing(3),
            p: theme.spacing(2),
            boxShadow: 'none',
            borderRadius: theme.spacing(3),
          }}
        >
          <CardHeader
            sx={{ borderBottom: `1px solid ${theme.palette.alphas.divider}` }}
            title={<Typography variant="body1">{t('supportForms.view.preview')}</Typography>}
          />
          <CardContent>
            {agencyId && title && description && fields.length > 0 && (
              <SupportForm
                title={title}
                description={description}
                customLogoUrl={previewUrl ?? customLogoUrl}
                agencyId={agencyId}
                workflowId={workflow}
                editing
              />
            )}
          </CardContent>
        </Card>
      </Box>
    </DynamicFieldsProvider>
  );
};
